<script lang="ts">
	import '@fontsource-variable/lora';
	import '@fontsource-variable/lora/wght-italic.css';
	import '@fontsource-variable/dm-sans';
	import '@fontsource/caveat';
	import '../app.css';
	import AppBar from '$lib/components/AppBar.svelte';
	import Footer from '$lib/components/Footer.svelte';
	import CookieConsent from '$lib/components/CookieConsent.svelte';
	import ConsentPrompt from '$lib/components/ConsentPrompt.svelte';
	import Toast from '$lib/components/ui/Toast.svelte';
	import Confirm from '$lib/components/ui/Confirm.svelte';
	import { authState } from '$lib/stores/auth.svelte';
	import { subnav } from '$lib/stores/subnav.svelte';
	import { getOnboardingStatus, trackFunnelEvent } from '$lib/api';
	import { page } from '$app/stores';
	import { browser } from '$app/environment';
	import { afterNavigate, goto, replaceState } from '$app/navigation';
	import { toast } from '$lib/stores/toast.svelte';
	import BillingBanner from '$lib/components/BillingBanner.svelte';
	import ActivationPaywallModal from '$lib/components/onboarding/ActivationPaywallModal.svelte';

	let { children } = $props();

	const { user } = authState;
	let currentPath = $derived($page.url.pathname);

	// /generateur-bulletin est à la fois landing SEO et outil app : sa surface
	// dépend de l'état et non de la route — connecté = chrome app, anonyme =
	// surface marketing (footer, donc mentions légales/CGU accessibles).
	// Défaut pendant l'init Firebase ($user encore null) : surface marketing,
	// identique au HTML prérendu — aucun flash pour le visiteur SEO anonyme ;
	// le connecté voit le chrome app apparaître à la résolution de l'auth,
	// sur rechargement complet uniquement.
	const appPaths = ['/app', '/journal'];
	// /sonometre rejoint les surfaces à double visage : chrome d'app pour un
	// connecté (sans quoi l'outil éjecte de l'espace), page publique avec son
	// pied de page — donc ses mentions légales — pour un visiteur.
	const authDependentAppPaths = ['/generateur-bulletin', '/sonometre'];
	let isAppSurface = $derived(
		appPaths.some(p => currentPath === p || currentPath.startsWith(p + '/'))
		|| (!!$user && authDependentAppPaths.some(p => currentPath === p || currentPath.startsWith(p + '/')))
	);
	let showAppNav = $derived(!!$user && isAppSurface);
	// Le mode classe est projeté devant les élèves : ni chrome, ni invitation,
	// ni mur d'activation ne doivent s'y afficher (l'outil est gratuit).
	let isClasseSurface = $derived(currentPath === '/app/classe' || currentPath.startsWith('/app/classe/'));

	// Le cahier journal a besoin d'une hauteur bornée (chaîne flex-1 jusqu'au calendrier).
	// Les autres surfaces app ont du contenu qui scroll naturellement.
	let isJournalSurface = $derived(currentPath === '/journal' || currentPath.startsWith('/journal/'));

	// Activation gate : mur d'engagement (≥3 semaines/séquences OU ≥7 jours) calculé
	// côté backend. On le récupère une fois par utilisateur connecté sur surface app.
	let gateOpen = $state(false);
	let gateStats = $state<{
		weeksCount: number;
		sequencesCount: number;
		progressionsCount: number;
		evaluationsCount: number;
		bulletinsCount: number;
		hoursSaved: number;
	}>({
		weeksCount: 0,
		sequencesCount: 0,
		progressionsCount: 0,
		evaluationsCount: 0,
		bulletinsCount: 0,
		hoursSaved: 0
	});
	let gateCheckedFor: string | null = null;
	// Retour d'un paiement Stripe abandonné (cancel_url de createCheckoutSession).
	// Lu sur window : les pages prérendues n'exposent pas les paramètres d'URL.
	const checkoutAnnule =
		browser && new URLSearchParams(window.location.search).get('checkout') === 'annule';
	// Échap ou « accès limité » : ne pas relancer le gate à chaque navigation.
	// Ni au retour d'un paiement abandonné : l'enseignant vient de dire non.
	let gateDismissed = checkoutAnnule;

	// replaceState lève une erreur tant que le routeur n'a pas démarré : ni
	// onMount, ni le premier afterNavigate (appelé juste avant le démarrage)
	// ne suffisent. `complete` se résout, lui, une fois le routeur prêt.
	let retourTraite = false;
	afterNavigate(({ complete }) => {
		if (!checkoutAnnule || retourTraite) return;
		retourTraite = true;
		complete.then(() => {
			const url = new URL(window.location.href);
			url.searchParams.delete('checkout');
			replaceState(`${url.pathname}${url.search}${url.hash}`, {});
			toast.info("Paiement annulé : rien n'a été prélevé. Une question ?", 10000, {
				label: 'Écrivez-moi',
				run: () => goto('/contact')
			});
		});
	});

	// Distingue le renoncement volontaire de l'expiration silencieuse à 24 h
	// (checkout_expired). Attend l'utilisateur : sans token, l'event est perdu.
	let annulationSignalee = false;
	$effect(() => {
		if (!checkoutAnnule || annulationSignalee || !$user) return;
		annulationSignalee = true;
		trackFunnelEvent('checkout_cancelled', { path: window.location.pathname });
	});

	function checkActivationGate(uid: string, key: string, canRetry = true) {
		getOnboardingStatus()
			.then((s) => {
				if (s.show_activation_gate) {
					gateStats = {
						weeksCount: s.weeks_count,
						sequencesCount: s.sequences_count,
						progressionsCount: s.progressions_count,
						evaluationsCount: s.evaluations_count,
						bulletinsCount: s.bulletins_count,
						hoursSaved: s.hours_saved
					};
					gateOpen = true;
				}
			})
			.catch(() => {
				// Le token Firebase peut ne pas être prêt au premier rendu. Sans
				// relâcher le verrou, un seul échec masque le gate pour toute la session.
				gateCheckedFor = null;
				if (!canRetry) return;
				setTimeout(() => {
					if (gateCheckedFor !== null || $user?.uid !== uid) return;
					gateCheckedFor = key;
					checkActivationGate(uid, key, false);
				}, 3000);
			});
	}

	$effect(() => {
		const uid = $user?.uid ?? null;
		if (!uid || !isAppSurface || isClasseSurface || gateDismissed) return;
		// Clé = uid + surface : l'éligibilité franchie en cours de session (3e séquence,
		// 7e jour) ou un statut en échec au chargement sont rattrapés à la navigation
		// suivante, au lieu d'être perdus jusqu'au prochain rechargement complet.
		const key = `${uid}:${currentPath}`;
		if (gateCheckedFor === key) return;
		gateCheckedFor = key;
		checkActivationGate(uid, key);
	});

	// La hauteur réelle du chrome (en-tête, plus le bandeau de facturation quand
	// il est là), publiée en `--chrome-h` pour les surfaces qui doivent se caler dessus :
	// le calendrier du journal, l'éditeur de plan de classe. Mesurée plutôt que
	// devinée — c'est précisément ce qui a permis à ces deux surfaces de suivre
	// l'arrivée puis le retrait de la sous-navigation sans qu'on y retouche.
	let chromeHeight = $state(0);
</script>

<svelte:head>
	<!-- Entité site : Organization + WebSite (#organization, #website, #logo).
	     Toutes les pages y font référence par @id — une seule source de vérité. -->
	{@html `<script type="application/ld+json">{"@context":"https://schema.org","@graph":[{"@type":"Organization","@id":"https://fichesequence.fr/#organization","name":"Fiche Séquence","alternateName":"FicheSequence","url":"https://fichesequence.fr/","logo":{"@type":"ImageObject","@id":"https://fichesequence.fr/#logo","url":"https://fichesequence.fr/favicon-v2-512x512.png","width":512,"height":512,"caption":"Fiche Séquence"},"image":{"@id":"https://fichesequence.fr/#logo"},"description":"Boîte à outils IA pour les professeurs des écoles françaises : fiches séquences, cahier journal, progressions annuelles, évaluations et appréciations de bulletin, alignés sur les programmes officiels de la maternelle au CM2.","areaServed":{"@type":"Country","name":"France"},"knowsLanguage":"fr","knowsAbout":["Programmes de l'école primaire française","Préparation de séquences pédagogiques","Cahier journal","Progression annuelle","Livret scolaire unique"],"contactPoint":{"@type":"ContactPoint","contactType":"customer support","url":"https://fichesequence.fr/contact","availableLanguage":"fr","areaServed":"FR"}},{"@type":"WebSite","@id":"https://fichesequence.fr/#website","url":"https://fichesequence.fr/","name":"Fiche Séquence","inLanguage":"fr-FR","publisher":{"@id":"https://fichesequence.fr/#organization"}}]}<\/script>`}
</svelte:head>

<div
	class="flex min-h-screen flex-col overflow-x-clip bg-paper font-body text-ink {isJournalSurface ? 'md:h-dvh md:overflow-hidden' : ''}"
	style="--chrome-h: {chromeHeight}px"
>
	<a
		href="#main-content"
		class="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded focus:bg-paper focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-ink focus:shadow-overlay focus:outline focus:outline-2 focus:outline-encre"
	>
		Aller au contenu principal
	</a>
	<div bind:clientHeight={chromeHeight} hidden={isClasseSurface}>
		<AppBar />

		<!-- Facturation : facture à régler ou carte qui expire. Pas sur la page
		     abonnement, qui porte la même alerte avec plus de détail. -->
		{#if $user && isAppSurface && currentPath !== '/app/abonnement'}
			<BillingBanner />
		{/if}

		{#if showAppNav && subnav.actions}
			<!-- Actions contextuelles de page. Le store n'est écrit par aucune page
			     aujourd'hui ; la zone est conservée en attendant son sort, sur sa
			     propre ligne plutôt que comprimée contre la navigation. -->
			<div class="no-print border-b border-mist bg-paper">
				<div class="container mx-auto flex items-center justify-end gap-1 px-4 py-1.5">
					{@render subnav.actions()}
				</div>
			</div>
		{/if}
	</div>

	<main id="main-content" class="flex-1 {isJournalSurface ? 'md:min-h-0 md:overflow-hidden' : ''}">
		{@render children()}
	</main>

	{#if !isAppSurface}
		<Footer />
	{/if}
	<CookieConsent />
{#if !isClasseSurface}
		<ConsentPrompt />
	{/if}
	<Toast />
	<Confirm />
	<ActivationPaywallModal
		open={gateOpen && !isClasseSurface}
		weeksCount={gateStats.weeksCount}
		sequencesCount={gateStats.sequencesCount}
		progressionsCount={gateStats.progressionsCount}
		evaluationsCount={gateStats.evaluationsCount}
		bulletinsCount={gateStats.bulletinsCount}
		hoursSaved={gateStats.hoursSaved}
		onClose={() => {
			gateOpen = false;
			gateDismissed = true;
		}}
	/>
</div>
